import React, { useState } from "react";
import SubscriptionCard from "components/Cards/subscriptionCard";
import JourneysCarousel from "components/CustomWidgets/journeysCarousel";
import OverviewBlock from "components/CustomWidgets/overviewBlock";
import ActivitySummary from "../ActivitySummary";
import BasicProfileCard from "components/Cards/BasicProfileCard";
import { AiRecommendation } from "assets/icons";
import { completeProfileList } from "components/HomePage/helper";
import BlogCard from "components/Cards/blogCard";
import JewelryBannerCard from "components/Cards/jewelryBannerCard";
import OverViewCarousal from "components/CustomWidgets/overViewCarousal";
import CompleteProfileCard from "components/Cards/completeProfileCard";
import GoPremiumModal from "components/CustomWidgets/goPremiumModal";
import {
  useBlogList,
  usePremiumMatches,
  useSuccessStories,
  useLatestProfile,
} from "hooks/api/useHome";
import { usePremium } from "context/PremiumContext";
import { useNavigate } from "react-router-dom";
import { useWeddingStore } from "hooks/api/useWedding";
import { useMyMatches, useNearMeMatches } from "hooks/api/useExplore";
import useMediaQuery from "hooks/useMediaQuery";

function RightSection({ profileData, isLoading }) {
  const { isPremium } = usePremium();
  const navigate = useNavigate();
  const [upgradeModal, setUpgradeModal] = useState(false);
  const isMobile = useMediaQuery("(max-width: 820px)");
  const handleSeeAllClick = (key) => {
    navigate(`/explore?tab=${key}`);
  };

  const handleUpgradeClick = () => {
    setUpgradeModal(true);
  };

  // Premium matches
  const { data: matches, isLoading: isPremiumLoading } = usePremiumMatches({
    page: 1,
    limit: 4,
  });
  const PremiumMatches = matches?.users_data;

  // ai recommended
  const { data: aiRecommended, isLoading: isAiRecommendLoading } =
    useNearMeMatches({ page: 1, limit: 4 });

  // Blogs
  const { data: blogData, isLoading: isBlogLoading } = useBlogList({
    see_all: true,
    request_type: 0,
  });

  // Wedding store
  const { data: storeData, isLoading: isWeddingLoading } = useWeddingStore();

  // Success stories
  const { data: stories = [], isLoading: isStoriesLoading } = useSuccessStories(
    { page: 1, limit: 6, see_all: false }
  );

  // recommended profiles
  const { data: recommended, isLoading: isrecommendedLoading } = useMyMatches({
    page: 1,
    limit: 4,
  });
  const RecommendedMatches = recommended?.users_data;

  // latest profiles
  const { data: latest, isLoading: islatestLoading } = useLatestProfile({
    page: 1,
    limit: 4,
  });
  const LatestProfiles = latest?.users_data;

  const completeProfile =
    profileData?.profile_completion_data?.profile_completion_status;
  const incompleteSections = completeProfileList.filter(
    (item) => completeProfile?.[item.key] === false
  );

  const ProfileListBlock = ({ profiles = [] }) => {
    return (
      <>
        {profiles.map((item) => {
          const isImageView =
            item?.is_private === true && item?.allow_image_view === false;
          return (
            <BasicProfileCard
              profile={item}
              key={item.id}
              snippetProfileId={item.id}
              snippetProfileName={item.name}
              snippetImage={item.profile_image}
              snippetProfileAge={item.age}
              snippetProfileHeight={item.height}
              snippetProfileJob={item.profession}
              snippetVisible={isImageView}
              snipperVerification={item?.verification_type}
              snippetPremium={item?.is_premium_member}
              snippetInterestSent={item?.has_expressed_interest}
              snippetFav={item?.has_favorited}
              snippetRecommended={item?.isSuggest}
              snippetImageRequested={item?.image_requested}
              aec_id={item?.aec_id}
            />
          );
        })}
      </>
    );
  };

  return (
    <div className="flex flex-col gap-16 right-section">
      <OverviewBlock
        isLoading={isLoading}
        cardTitle="Your activity summary"
        cardDesc="Stay updated on your interactions and engagement! "
        seeAllBtn={false}
        cardComponent={
          <ActivitySummary
            profileData={profileData}
            onUpgradeClick={handleUpgradeClick}
          />
        }
        data={profileData}
        className="home-title"
      />

      <OverviewBlock
        isLoading={isPremiumLoading}
        cardTitle="Premium matches"
        cardDesc="These profiles belong to premium members who are more serious about finding their perfect match."
        cardComponent={<ProfileListBlock profiles={PremiumMatches} />}
        onClick={() => handleSeeAllClick("4")}
        data={PremiumMatches}
        seeAllClick={() => navigate("/explore?tab=4")}
      />

      {profileData?.profile_completion_data?.profile_completion_percent !==
        100 && (
        <OverViewCarousal
          isLoading={isLoading}
          cardTitle="Complete your profile"
          cardDesc="A complete profile increases your chances of finding the perfect match!"
          tagLabel={`${profileData?.profile_completion_data?.profile_completion_percent}% Completed`}
          slidesToShow={2.9}
          carousalData={incompleteSections}
          carousalClass="complete-profile-carousal"
          renderItem={(item, i) => (
            <CompleteProfileCard
              buttonText={item?.btnText}
              cardImage={item?.image}
              cardContent={item?.content}
              href={item?.href}
            />
          )}
          carousalResponsive={[
            { breakpoint: 1390, settings: { slidesToShow: 2.9 } },
            { breakpoint: 1318, settings: { slidesToShow: 2.2 } },
            { breakpoint: 1210, settings: { slidesToShow: 2 } },
            { breakpoint: 1095, settings: { slidesToShow: 1.5 } },
            { breakpoint: 880, settings: { slidesToShow: 1.2 } },
            { breakpoint: 767, settings: { slidesToShow: 1.9 } },
            { breakpoint: 684, settings: { slidesToShow: 1.5 } },
            { breakpoint: 546, settings: { slidesToShow: 1.1 } },
          ]}
        />
      )}

      <OverviewBlock
        isLoading={isrecommendedLoading}
        cardTitle="Recommended today"
        showTimer
        cardDesc="Fresh recommendations to bring you closer to your perfect match!"
        cardComponent={<ProfileListBlock profiles={RecommendedMatches} />}
        onClick={() => handleSeeAllClick("5")}
        data={RecommendedMatches}
        seeAllClick={() => navigate("/explore?tab=5")}
      />

      {!isPremium && (
        <SubscriptionCard
          title="Unlock premium matches – Find your perfect partner faster!"
          data={[
            "Profile boost & premium card",
            "Advanced search & filters",
            "Matchmaker insights",
            "Unlimited message",
          ]}
          btnText="See membership plans"
        />
      )}

      <OverviewBlock
        isLoading={isAiRecommendLoading}
        cardTitle="AI Recommended"
        cardTitleIcon={<AiRecommendation />}
        cardDesc="Discover AI-recommended profiles customized to your preferences."
        cardComponent={
          <ProfileListBlock profiles={aiRecommended?.users_data} />
        }
        onClick={() => handleSeeAllClick("6")}
        data={aiRecommended?.users_data}
        seeAllClick={() => navigate("/explore?tab=5")}
      />

      <OverviewBlock
        isLoading={islatestLoading}
        cardTitle="Latest profiles"
        cardDesc="Check out newly joined members who are actively looking for their perfect match.."
        cardComponent={<ProfileListBlock profiles={LatestProfiles} />}
        onClick={() => handleSeeAllClick("3")}
        data={LatestProfiles}
        seeAllClick={() => navigate("/explore?tab=3")}
      />

      {!isStoriesLoading && (
        <JourneysCarousel
          successStories={stories}
          carousalResponsive={[
            { breakpoint: 1427, settings: { slidesToShow: 1.5 } },
            { breakpoint: 1277, settings: { slidesToShow: 1.2 } },
            { breakpoint: 1064, settings: { slidesToShow: 1 } },
            { breakpoint: 919, settings: { slidesToShow: 0.8 } },
            { breakpoint: 816, settings: { slidesToShow: 0.6 } },
            { breakpoint: 767, settings: { slidesToShow: 1.2 } },
            { breakpoint: 726, settings: { slidesToShow: 1 } },
          ]}
        />
      )}

      <OverViewCarousal
        isLoading={isWeddingLoading}
        carousalClass="store-carousal wedding-store-carousal"
        cardTitle="Wedding store"
        cardDesc="Sponsored"
        slidesToShow={2}
        autoplaySpeed={4000}
        autoplay={{ dotDuration: false }}
        carousalData={storeData}
        href="/wedding-store"
        renderItem={(item, i) => <JewelryBannerCard key={i} data={item} />}
        carousalResponsive={[
          { breakpoint: 1420, settings: { slidesToShow: 1.8 } },
          { breakpoint: 1050, settings: { slidesToShow: 1.5 } },
          { breakpoint: 900, settings: { slidesToShow: 1.2 } },
          { breakpoint: 767, settings: { slidesToShow: 1.5 } },
          { breakpoint: 450, settings: { slidesToShow: 1.2 } },
        ]}
      />

      <OverViewCarousal
        isLoading={isBlogLoading}
        carousalClass="store-carousal blog-carousal"
        cardTitle={isMobile ? "Match diaries" : "Blogs"}
        showSeeAllOnNoScroll={true}
        autoplaySpeed={4000}
        autoplay={{ dotDuration: false }}
        cardDesc="Explore our blogs to enhance your matchmaking experience."
        href="blog"
        slidesToShow={3}
        carousalData={blogData?.blog_data}
        renderItem={(item, i) => (
          <BlogCard key={i} blog={item} className="blog-card" />
        )}
        carousalResponsive={[
          { breakpoint: 1280, settings: { slidesToShow: 2.5 } },
          { breakpoint: 986, settings: { slidesToShow: 2 } },
          { breakpoint: 828, settings: { slidesToShow: 1.5 } },
          { breakpoint: 767, settings: { slidesToShow: 2.5 } },
          { breakpoint: 650, settings: { slidesToShow: 2 } },
          { breakpoint: 550, settings: { slidesToShow: 1.5 } },
        ]}
      />

      <GoPremiumModal
        open={upgradeModal}
        wrapClassName={`go-premium-primary`}
        contentTitle="Want to see profiles that are interested in you?"
        buttonTitle="Upgrade plan"
        onCancel={() => {
          setUpgradeModal(false);
        }}
        onOk={() => {
          setUpgradeModal(false);
        }}
      />
    </div>
  );
}

export default RightSection;
